import type {
  VirtualIpCharacterBatch,
  VirtualIpCharacterSelectionWorkspace,
  VirtualIpDraft,
  VirtualIpDraftStatus,
} from '../../api'
import { VIRTUAL_IP_FLOW_STEPS, VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import {
  deriveCharacterLineageFacts,
  draftCompletedThrough,
  type VirtualIpStepDependencyState,
  type VirtualIpStepStaleCause,
  virtualIpStepRegenerationReasonKey,
} from './virtualIpStepDependency'

/** @deprecated Use VirtualIpStepDependencyState — STALE kept for gradual migration. */
export type VirtualIpStepState = VirtualIpStepDependencyState | 'STALE'

export interface VirtualIpWorkflowSnapshot {
  draftStatus: VirtualIpDraftStatus
  positioningConfirmedAt?: string | null
  positioningProfileRevision?: number
  hasSuccessfulCharacterCandidates: boolean
  hasAnyCharacterArtifact: boolean
  characterGenerationInFlight: boolean
  hasCharacterSelection: boolean
  characterSelectionCurrent: boolean
  /** Unified character validity for new Step5. */
  characterNeedsRegeneration: boolean
  /** @deprecated Use characterNeedsRegeneration */
  step5NeedsRegeneration: boolean
  /** @deprecated Use characterNeedsRegeneration */
  step6NeedsRegeneration: boolean
  voiceConfirmed: boolean
  voiceReached: boolean
  voiceMode?: string | null
  workflowComplete: boolean
}

export interface VirtualIpWorkflowProgress {
  current_step: number
  highest_completed_step: number
  furthest_reached_step: number
  unlocked_steps: number[]
  completed_steps: number[]
  step_states: Record<number, VirtualIpStepDependencyState>
  step_state_reasons: Record<number, string>
  is_complete: boolean
}

export function isVirtualIpPositioningConfirmed(
  draft: Pick<VirtualIpDraft, 'status' | 'positioning_confirmed_at'>,
): boolean {
  return draft.status === 'positioning_confirmed' || Boolean(draft.positioning_confirmed_at)
}

export function buildVirtualIpWorkflowSnapshot(input: {
  draftStatus: VirtualIpDraftStatus
  positioningConfirmedAt?: string | null
  positioningProfileRevision?: number
  selectionWorkspace?: VirtualIpCharacterSelectionWorkspace | null
  latestBatch?: VirtualIpCharacterBatch | null
  voiceConfirmed?: boolean
  voiceReached?: boolean
  voiceMode?: string | null
  workflowComplete?: boolean
}): VirtualIpWorkflowSnapshot {
  const lineage = deriveCharacterLineageFacts({
    positioningProfileRevision: input.positioningProfileRevision,
    selectionWorkspace: input.selectionWorkspace,
    latestBatch: input.latestBatch,
  })

  return {
    draftStatus: input.draftStatus,
    positioningConfirmedAt: input.positioningConfirmedAt,
    positioningProfileRevision: input.positioningProfileRevision ?? 0,
    hasSuccessfulCharacterCandidates: lineage.hasSuccessfulCharacterCandidates,
    hasAnyCharacterArtifact: lineage.hasAnyCharacterArtifact,
    characterGenerationInFlight: lineage.characterGenerationInFlight,
    hasCharacterSelection: lineage.hasCharacterSelection,
    characterSelectionCurrent: lineage.characterSelectionCurrent,
    characterNeedsRegeneration: lineage.characterNeedsRegeneration,
    step5NeedsRegeneration: lineage.characterNeedsRegeneration,
    step6NeedsRegeneration: lineage.characterNeedsRegeneration,
    voiceConfirmed: Boolean(input.voiceConfirmed),
    voiceReached: Boolean(input.voiceReached),
    voiceMode: input.voiceMode ?? null,
    workflowComplete: Boolean(input.workflowComplete),
  }
}

function setRegeneration(
  states: Record<number, VirtualIpStepDependencyState>,
  reasons: Record<number, string>,
  step: number,
  cause: VirtualIpStepStaleCause,
) {
  states[step] = 'NEEDS_REGENERATION'
  reasons[step] = virtualIpStepRegenerationReasonKey(step, cause)
}

function buildStepStates(
  snapshot: VirtualIpWorkflowSnapshot,
  completed: number[],
): { states: Record<number, VirtualIpStepDependencyState>; reasons: Record<number, string> } {
  const states: Record<number, VirtualIpStepDependencyState> = {}
  const reasons: Record<number, string> = {}
  const through4 = draftCompletedThrough(snapshot.draftStatus, snapshot.positioningConfirmedAt)

  for (const step of VIRTUAL_IP_FLOW_STEPS.map((s) => s.id)) {
    if (step <= 4) {
      states[step] = step <= through4 && completed.includes(step) ? 'COMPLETED' : step <= through4 ? 'AVAILABLE' : 'LOCKED'
      continue
    }
    if (step === 5) {
      if (through4 < 4) states[step] = 'LOCKED'
      else if (snapshot.characterGenerationInFlight) states[step] = 'IN_PROGRESS'
      else if (snapshot.characterNeedsRegeneration) setRegeneration(states, reasons, 5, 'POSITIONING_REVISION')
      else if (completed.includes(5)) states[step] = 'COMPLETED'
      else states[step] = 'AVAILABLE'
      continue
    }
    if (step === 6) {
      if (!completed.includes(5) && !snapshot.hasCharacterSelection) states[step] = 'LOCKED'
      else if (snapshot.characterNeedsRegeneration) setRegeneration(states, reasons, 6, 'POSITIONING_REVISION')
      else if (completed.includes(6)) states[step] = 'COMPLETED'
      else states[step] = 'AVAILABLE'
      continue
    }
    if (step === 7) {
      if (!completed.includes(6) && !snapshot.voiceConfirmed && !snapshot.voiceReached) states[step] = 'LOCKED'
      else if (snapshot.characterNeedsRegeneration) setRegeneration(states, reasons, 7, 'POSITIONING_REVISION')
      else if (completed.includes(7)) states[step] = 'COMPLETED'
      else states[step] = 'AVAILABLE'
    }
  }
  return { states, reasons }
}

export function resolveVirtualIpWorkflowProgress(snapshot: VirtualIpWorkflowSnapshot): VirtualIpWorkflowProgress {
  const completed: number[] = []
  const through4 = draftCompletedThrough(snapshot.draftStatus, snapshot.positioningConfirmedAt)
  for (let s = 1; s <= through4; s += 1) completed.push(s)

  const step5Valid =
    through4 >= 4 &&
    snapshot.hasCharacterSelection &&
    snapshot.characterSelectionCurrent &&
    !snapshot.characterNeedsRegeneration
  if (step5Valid) completed.push(5)

  if (completed.includes(5) && snapshot.voiceConfirmed) completed.push(6)
  if (completed.includes(6) && snapshot.workflowComplete) completed.push(7)

  const highest_completed_step = completed.length ? Math.max(...completed) : 0

  let furthest_reached_step = through4
  if (through4 >= 4) furthest_reached_step = Math.max(furthest_reached_step, 5)
  if (snapshot.hasAnyCharacterArtifact || snapshot.hasSuccessfulCharacterCandidates) {
    furthest_reached_step = Math.max(furthest_reached_step, 5)
  }
  if (snapshot.hasCharacterSelection) furthest_reached_step = Math.max(furthest_reached_step, 5)
  if (completed.includes(5) || step5Valid) furthest_reached_step = Math.max(furthest_reached_step, 6)
  if (snapshot.voiceReached || snapshot.voiceConfirmed) {
    furthest_reached_step = Math.max(furthest_reached_step, snapshot.voiceConfirmed ? 7 : completed.includes(5) ? 7 : 6)
  }
  if (snapshot.workflowComplete) furthest_reached_step = Math.max(furthest_reached_step, 7)
  furthest_reached_step = Math.max(furthest_reached_step, highest_completed_step)

  const unlocked = new Set<number>()
  for (let s = 1; s <= furthest_reached_step; s += 1) unlocked.add(s)
  if (highest_completed_step < VIRTUAL_IP_FLOW_TOTAL_STEPS) unlocked.add(highest_completed_step + 1)
  if (through4 >= 4) unlocked.add(5)
  for (const s of completed) unlocked.add(s)

  let current_step: number
  if (snapshot.workflowComplete || completed.includes(7)) {
    current_step = 7
  } else if (snapshot.voiceConfirmed) {
    current_step = 7
  } else if (step5Valid) {
    current_step = 6
  } else if (through4 >= 4) {
    current_step = 5
  } else {
    current_step = Math.min(highest_completed_step + 1, VIRTUAL_IP_FLOW_TOTAL_STEPS) || 1
  }

  const { states: step_states, reasons: step_state_reasons } = buildStepStates(snapshot, completed)

  return {
    current_step,
    highest_completed_step,
    furthest_reached_step,
    unlocked_steps: Array.from(unlocked).sort((a, b) => a - b),
    completed_steps: completed,
    step_states,
    step_state_reasons,
    is_complete: completed.includes(7),
  }
}

export function virtualIpCompletedThrough(
  status: VirtualIpDraftStatus,
  characterBatchStatus?: string | null,
): number {
  const snapshot: VirtualIpWorkflowSnapshot = {
    draftStatus: status,
    hasSuccessfulCharacterCandidates: characterBatchStatus === 'SUCCEEDED',
    hasAnyCharacterArtifact: Boolean(characterBatchStatus),
    characterGenerationInFlight: false,
    hasCharacterSelection: false,
    characterSelectionCurrent: false,
    characterNeedsRegeneration: false,
    step5NeedsRegeneration: false,
    step6NeedsRegeneration: false,
    voiceConfirmed: false,
    voiceReached: false,
    workflowComplete: false,
  }
  return resolveVirtualIpWorkflowProgress(snapshot).highest_completed_step
}

export function virtualIpStepRouteSegment(step: number): string | null {
  return VIRTUAL_IP_FLOW_STEPS.find((s) => s.id === step)?.route ?? null
}

export function virtualIpResumePath(
  workspaceId: string,
  progress: VirtualIpWorkflowProgress,
  projectId?: string,
): string {
  const preferred = progress.is_complete ? 7 : progress.current_step
  const route =
    virtualIpStepRouteSegment(preferred) ??
    [...progress.unlocked_steps]
      .reverse()
      .map((s) => virtualIpStepRouteSegment(s))
      .find((r) => r != null) ??
    'step-1'
  if (projectId) {
    return `/workspace/${workspaceId}/virtual-ip/projects/${projectId}/${route}`
  }
  return `/workspace/${workspaceId}/virtual-ip-positioning/${route}`
}

export function isVirtualIpStepCompleted(progress: VirtualIpWorkflowProgress, step: number): boolean {
  return progress.completed_steps.includes(step)
}

export function isVirtualIpStepStale(progress: VirtualIpWorkflowProgress, step: number): boolean {
  return progress.step_states[step] === 'NEEDS_REGENERATION'
}

export function isVirtualIpStepNeedsRegeneration(progress: VirtualIpWorkflowProgress, step: number): boolean {
  return isVirtualIpStepStale(progress, step)
}

export function virtualIpStepStateReason(progress: VirtualIpWorkflowProgress, step: number): string | null {
  return progress.step_state_reasons[step] ?? null
}

export function isVirtualIpStepUnlocked(progress: VirtualIpWorkflowProgress, step: number): boolean {
  return (
    progress.unlocked_steps.includes(step) ||
    progress.completed_steps.includes(step) ||
    step <= progress.furthest_reached_step
  )
}

export function virtualIpDownstreamSteps(fromStep: number): number[] {
  return VIRTUAL_IP_FLOW_STEPS.map((s) => s.id).filter((id) => id > fromStep)
}

export const VIRTUAL_IP_UPSTREAM_EDIT_WARNING_KEY = 'upstreamEdit.warning'

export const VIRTUAL_IP_STEP4_UPSTREAM_EDIT_TITLE_KEY = 'upstreamEdit.step4Title'

export const VIRTUAL_IP_DOWNSTREAM_STEP_LABEL_KEYS: Record<number, string> = {
  5: 'downstreamSteps.5',
  6: 'downstreamSteps.6',
  7: 'downstreamSteps.7',
}

/** @deprecated Use VIRTUAL_IP_UPSTREAM_EDIT_WARNING_KEY with i18n. */
export const VIRTUAL_IP_UPSTREAM_EDIT_WARNING = VIRTUAL_IP_UPSTREAM_EDIT_WARNING_KEY

/** @deprecated Use VIRTUAL_IP_STEP4_UPSTREAM_EDIT_TITLE_KEY with i18n. */
export const VIRTUAL_IP_STEP4_UPSTREAM_EDIT_TITLE = VIRTUAL_IP_STEP4_UPSTREAM_EDIT_TITLE_KEY

export function virtualIpDownstreamStepLabelKeys(fromStep: number): string[] {
  return virtualIpDownstreamSteps(fromStep)
    .map((id) => VIRTUAL_IP_DOWNSTREAM_STEP_LABEL_KEYS[id])
    .filter((key): key is string => Boolean(key))
}

/** @deprecated Use virtualIpDownstreamStepLabelKeys with i18n at render time. */
export function virtualIpDownstreamStepLabels(fromStep: number): string[] {
  return virtualIpDownstreamStepLabelKeys(fromStep)
}

export function virtualIpFirstRegenerationNotice(
  progress: VirtualIpWorkflowProgress,
): { step: number; reasonKey: string } | null {
  for (const step of VIRTUAL_IP_FLOW_STEPS.map((s) => s.id)) {
    const reasonKey = progress.step_state_reasons[step]
    if (reasonKey) return { step, reasonKey }
  }
  return null
}
